// Feuille de stats d'un match (D-046) : tout se calcule à partir du journal des actions
// saisies en direct (annuler = effacer la dernière). Évaluation façon FFBB :
// points + rebonds + passes + interceptions + contres − tirs manqués − balles perdues.
// La table de marque (D-175, D-176) écrit dans le MÊME journal : fautes techniques, disruptives et flagrantes (règles
// FIBA 2026 : la faute antisportive n'existe plus), fautes d'équipe sans joueuse, temps-morts, l'adversaire par numéro
// (`opp_number`), le lancer franc du 3x3 et le « −1 » qui corrige ; `game_ms` : le temps restant dans la période.

export type ActionKind = 'p2' | 'p2_miss' | 'p3' | 'p3_miss' | 'ft' | 'ft_miss' | 'oreb' | 'dreb' | 'ast' | 'stl' | 'blk' | 'tov' | 'foul'
  | 'us1' | 'us2' | 'us3' | 'opp1' | 'opp2' | 'opp3' | 'opp_foul'
  | 'foul_t' | 'foul_di' | 'foul_fl' | 'us_foul' | 'us_to' | 'us_ft' | 'us_fix'
  | 'opp_foul_t' | 'opp_foul_di' | 'opp_foul_fl' | 'opp_to' | 'opp_ft' | 'opp_fix'
  | 'clock_fix' | 'shot_fix';

export interface Action {
  id: string; person_id: string | null; kind: ActionKind; quarter: number; created_at: string;
  /** Table de marque : le numéro du joueur adverse ; le temps restant dans la période (ms). */
  opp_number?: number | null; game_ms?: number | null;
  /** Une correction du chrono (clock_fix, shot_fix) : avant / après (ms), la période avant / après. */
  meta?: { from?: number; to?: number; q_from?: number; q_to?: number } | null;
}

export const ACTION_LABEL: Record<ActionKind, string> = {
  p2: '+2', p2_miss: '2 pts manqué', p3: '+3', p3_miss: '3 pts manqué', ft: 'Lancer franc', ft_miss: 'Lancer franc manqué',
  oreb: 'Rebond offensif', dreb: 'Rebond défensif', ast: 'Passe décisive', stl: 'Interception', blk: 'Contre', tov: 'Balle perdue', foul: 'Faute',
  us1: 'Nous +1', us2: 'Nous +2', us3: 'Nous +3',
  opp1: 'Adversaire +1', opp2: 'Adversaire +2', opp3: 'Adversaire +3', opp_foul: 'Faute adverse',
  foul_t: 'Faute technique', foul_di: 'Faute disruptive', foul_fl: 'Faute flagrante', us_foul: 'Faute d’équipe', us_to: 'Temps-mort',
  us_ft: 'Nous · lancer franc', us_fix: 'Nous −1',
  opp_foul_t: 'Faute technique adverse', opp_foul_di: 'Faute disruptive adverse', opp_foul_fl: 'Faute flagrante adverse', opp_to: 'Temps-mort adverse',
  opp_ft: 'Adversaire · lancer franc', opp_fix: 'Adversaire −1', clock_fix: 'Chrono corrigé', shot_fix: 'Chrono des tirs corrigé',
};

// us1…us3 : la saisie « score seul » (D-055), des points de l'équipe sans joueuse.
// us_ft, opp_ft : le lancer franc du 3x3 ; us_fix, opp_fix : le « −1 » qui corrige (table de marque).
const PTS: Partial<Record<ActionKind, number>> = { p2: 2, p3: 3, ft: 1, us1: 1, us2: 2, us3: 3, us_ft: 1, us_fix: -1 };
const OPP: Partial<Record<ActionKind, number>> = { opp1: 1, opp2: 2, opp3: 3, opp_ft: 1, opp_fix: -1 };
/** Les points d'une action et de quel côté (le « −1 » vaut −1). */
export const pointsOf = (kind: ActionKind): { side: 'us' | 'them'; pts: number } | null =>
  PTS[kind] !== undefined ? { side: 'us', pts: PTS[kind]! } : OPP[kind] !== undefined ? { side: 'them', pts: OPP[kind]! } : null;

export function score(actions: Pick<Action, 'kind'>[]): { us: number; them: number } {
  return actions.reduce((s, a) => ({ us: s.us + (PTS[a.kind] ?? 0), them: s.them + (OPP[a.kind] ?? 0) }), { us: 0, them: 0 });
}

/** Score par quart-temps (1 à 4, puis prolongations). */
export function byQuarter(actions: Pick<Action, 'kind' | 'quarter'>[]): { q: number; us: number; them: number }[] {
  const qs = [...new Set(actions.map(a => a.quarter))].sort((a, b) => a - b);
  return qs.map(q => ({ q, ...score(actions.filter(a => a.quarter === q)) }));
}

export interface Line {
  person_id: string; pts: number; p2: [number, number]; p3: [number, number]; ft: [number, number];
  oreb: number; dreb: number; reb: number; ast: number; stl: number; blk: number; tov: number; fouls: number; eval: number;
}

export function boxScore(actions: Pick<Action, 'kind' | 'person_id'>[]): Line[] {
  const by = new Map<string, Line>();
  const get = (pid: string) => {
    let l = by.get(pid);
    if (!l) { l = { person_id: pid, pts: 0, p2: [0, 0], p3: [0, 0], ft: [0, 0], oreb: 0, dreb: 0, reb: 0, ast: 0, stl: 0, blk: 0, tov: 0, fouls: 0, eval: 0 }; by.set(pid, l); }
    return l;
  };
  for (const a of actions) {
    if (!a.person_id) continue;
    const l = get(a.person_id);
    switch (a.kind) {
      case 'p2': l.p2[0]++; l.p2[1]++; l.pts += 2; break;
      case 'p2_miss': l.p2[1]++; break;
      case 'p3': l.p3[0]++; l.p3[1]++; l.pts += 3; break;
      case 'p3_miss': l.p3[1]++; break;
      case 'ft': l.ft[0]++; l.ft[1]++; l.pts += 1; break;
      case 'ft_miss': l.ft[1]++; break;
      case 'oreb': l.oreb++; break;
      case 'dreb': l.dreb++; break;
      case 'ast': l.ast++; break;
      case 'stl': l.stl++; break;
      case 'blk': l.blk++; break;
      case 'tov': l.tov++; break;
      case 'foul': case 'foul_t': case 'foul_di': case 'foul_fl': l.fouls++; break;   // toutes comptent dans les 5 (FIBA art. 41)
      default: break;
    }
  }
  return [...by.values()].map(l => {
    const reb = l.oreb + l.dreb;
    const missed = (l.p2[1] - l.p2[0]) + (l.p3[1] - l.p3[0]) + (l.ft[1] - l.ft[0]);
    return { ...l, reb, eval: l.pts + reb + l.ast + l.stl + l.blk - missed - l.tov };
  }).sort((a, b) => b.pts - a.pts || b.eval - a.eval);
}
